<script lang="ts">
  import {tooltip} from '@/actions/tippy-action';
  import {getInstance} from '@/services/animation';
  import chroma from 'chroma-js';
  import {onDestroy, onMount} from 'svelte';
  import AlwanColorPicker from '../../../../components/alwan-color-picker/AlwanColorPicker.svelte';
  import editorInstance from '../../editor';
  import editorStore from '../../editor.store';

  let active: HTMLElement;
  let target: HTMLElement;
  let textColorPreviewElement: HTMLElement;
  let backgroundColorPreviewElement: HTMLElement;
  let textOutlinePreviewColor: string;
  let container: HTMLElement;

  let textOutlineSize = 0;
  let isTextOutlineThicknessMenuOpen = false;
  let changeOutlineColor: (color: string) => any;
  let t = 0;

  function changeBackgroundColor(e: CustomEvent) {
    const color = e.detail.color;
    if (active) active.style.backgroundColor = color;
    if (active && backgroundColorPreviewElement)
      backgroundColorPreviewElement.style.backgroundColor = active.style.backgroundColor;
    editorInstance.history.addHistory('background color change');
    getInstance().templateLoader.colorLoader.readColors();
  }

  const unsubscribe = editorStore.subscribe(store => {
    if (store.activeComponent == active) return;
    if (!active) isTextOutlineThicknessMenuOpen = false;
    active = store.activeComponent as HTMLElement;
    target = active?.querySelector('.dynamic') as HTMLElement;
    if (!target) return;

    const textStrokeSplit = target.style.webkitTextStroke.split(' ');
    textOutlineSize = parseFloat(
      textStrokeSplit
        .splice(0, 1)
        .filter(c => !isNaN(parseInt(c)) || c === '.')
        .join('')
    );
    t = textOutlineSize;
    textOutlinePreviewColor = textStrokeSplit.join('') || 'black';
  });

  function changeColor(e: CustomEvent) {
    const color = e.detail.color;
    if (target) target.style.color = color;
    textColorPreviewElement.style.color = color;
    textColorPreviewElement.style.backgroundColor = color;
    if (target.style.webkitTextFillColor) {
      target.style.webkitTextFillColor = color;
    }
    editorInstance.history.addHistory('color change');
    getInstance().templateLoader.colorLoader.readColors();
  }

  $: colorDefault = target?.style?.color === 'transparent' ? '#000000' : target?.style?.color;

  onMount(() => {
    textColorPreviewElement.style.backgroundColor = target.style.color;
    if (active) backgroundColorPreviewElement.style.backgroundColor = active.style.backgroundColor;
  });

  function openTextOutlineMenu(event: any) {
    isTextOutlineThicknessMenuOpen = !isTextOutlineThicknessMenuOpen;
  }

  function updateTextOutline() {
    textOutlineSize = t * parseFloat(target.style.fontSize);
    outlineResize(textOutlineSize);
    editorInstance.history.addHistory('Update Text Outline');
  }

  function outlineColorChange(e: any) {
    if (typeof e.detail !== 'object') return;
    const color = typeof e.detail.color === 'function' ? e.detail.color() : e.detail.color;
    textOutlinePreviewColor = color;
    if (textOutlineSize === 0) textOutlineSize = 0.1;
    outlineResize(textOutlineSize);
  }

  function outlineResize(newTextOutlineSize: number) {
    if (!textOutlinePreviewColor || textOutlinePreviewColor === '') return;
    if (newTextOutlineSize < 0) return;
    console.log('outlineResize', newTextOutlineSize);
    const color = textOutlinePreviewColor;
    target.style.webkitTextStroke = `${newTextOutlineSize}px ${color}`;
    target.style.paintOrder = 'stroke fill';
  }

  function detectWindowClick(e: MouseEvent) {
    const target = e.target;
    if (!target) return;
    if (!container.contains(target as HTMLElement)) {
      isTextOutlineThicknessMenuOpen = false;
    }
    return;
  }

  onDestroy(unsubscribe);
</script>

<div class="flex justify-between gap-x-3">
  <div class="btn relative">
    <AlwanColorPicker {colorDefault} on:change-color={changeColor}>
      <div class="flex h-full w-full flex-col items-center justify-center gap-y-1 px-1" use:tooltip={'Text Color'}>
        <i class="fa-solid fa-font align-middle"></i>
        <div class="h-[6px] w-7 rounded border border-gray-300" bind:this={textColorPreviewElement}></div>
      </div>
    </AlwanColorPicker>
  </div>
  <div class="btn relative">
    <AlwanColorPicker {colorDefault} on:change-color={changeBackgroundColor}>
      <div
        class="flex h-full flex-col items-center justify-center rounded-md border border-gray-300 bg-gray-500 px-2 text-white"
        bind:this={backgroundColorPreviewElement}
        use:tooltip={'Background Color'}
      >
        <i
          class="fa-solid fa-font align-middle"
          style={`color: ${textColorPreviewElement?.style.color || colorDefault}`}
        ></i>
      </div>
    </AlwanColorPicker>
  </div>

  <div class="colorBlock">
    <AlwanColorPicker
      bind:changeColor={changeOutlineColor}
      colorDefault={textOutlinePreviewColor}
      on:change-color={outlineColorChange}
    >
      <div
        class="flex h-full w-full items-center justify-center rounded-md border border-gray-300 px-2"
        use:tooltip={'Outline Color'}
        style={`background-color: ${backgroundColorPreviewElement?.style.backgroundColor || colorDefault}`}
      >
        <svg
          class="drops-hadow-md"
          width="18"
          height="24"
          viewBox="0 0 17 17"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <mask
            id="path-1-outside-1_767_91"
            maskUnits="userSpaceOnUse"
            x="-3.8147e-06"
            y="1.14441e-05"
            width="17"
            height="17"
            fill="black"
          >
            <rect fill="white" x="-3.8147e-06" y="1.14441e-05" width="17" height="17" />
            <path
              d="M8.90163 2.54848C8.82455 2.38449 8.70238 2.24584 8.5494 2.14871C8.39643 2.05159 8.21897 2.00001 8.03777 2.00001C7.85657 2.00001 7.67911 2.05159 7.52613 2.14871C7.37316 2.24584 7.25099 2.38449 7.17391 2.54848L2.083 13.3667C1.98108 13.5948 1.9727 13.8538 2.05964 14.0881C2.14657 14.3224 2.32191 14.5132 2.54798 14.6196C2.77406 14.726 3.03286 14.7396 3.2688 14.6573C3.50475 14.575 3.69903 14.4035 3.80993 14.1796L5.05004 11.5459H11.0255L12.2648 14.1796C12.3726 14.4087 12.567 14.5856 12.8053 14.6714C13.0435 14.7572 13.306 14.7448 13.5352 14.637C13.7643 14.5292 13.9412 14.3348 14.0269 14.0966C14.1127 13.8583 14.1003 13.5958 13.9925 13.3667L8.90163 2.54848ZM5.94891 9.63678L8.03777 5.19655L10.1274 9.63678H5.94891Z"
            />
          </mask>
          <path
            d="M8.90163 2.54848C8.82455 2.38449 8.70238 2.24584 8.5494 2.14871C8.39643 2.05159 8.21897 2.00001 8.03777 2.00001C7.85657 2.00001 7.67911 2.05159 7.52613 2.14871C7.37316 2.24584 7.25099 2.38449 7.17391 2.54848L2.083 13.3667C1.98108 13.5948 1.9727 13.8538 2.05964 14.0881C2.14657 14.3224 2.32191 14.5132 2.54798 14.6196C2.77406 14.726 3.03286 14.7396 3.2688 14.6573C3.50475 14.575 3.69903 14.4035 3.80993 14.1796L5.05004 11.5459H11.0255L12.2648 14.1796C12.3726 14.4087 12.567 14.5856 12.8053 14.6714C13.0435 14.7572 13.306 14.7448 13.5352 14.637C13.7643 14.5292 13.9412 14.3348 14.0269 14.0966C14.1127 13.8583 14.1003 13.5958 13.9925 13.3667L8.90163 2.54848ZM5.94891 9.63678L8.03777 5.19655L10.1274 9.63678H5.94891Z"
            fill={textColorPreviewElement?.style.color || colorDefault}
          />
          <path
            d="M8.90163 2.54848L7.09163 3.39931L7.092 3.40008L8.90163 2.54848ZM7.17391 2.54848L8.98354 3.40008L8.98391 3.39931L7.17391 2.54848ZM2.083 13.3667L0.273359 12.5151L0.264952 12.5329L0.2569 12.551L2.083 13.3667ZM3.80993 14.1796L5.60217 15.0672L5.61095 15.0495L5.61938 15.0316L3.80993 14.1796ZM5.05004 11.5459V9.54587H3.78114L3.24059 10.6939L5.05004 11.5459ZM11.0255 11.5459L12.8352 10.6943L12.2947 9.54587H11.0255V11.5459ZM12.2648 14.1796L14.0745 13.3281L14.0745 13.3281L12.2648 14.1796ZM13.9925 13.3667L15.8022 12.5152L15.8022 12.5151L13.9925 13.3667ZM5.94891 9.63678L4.13917 8.7854L2.79776 11.6368H5.94891V9.63678ZM8.03777 5.19655L9.84738 4.34491L8.03743 0.499003L6.22803 4.34518L8.03777 5.19655ZM10.1274 9.63678V11.6368H13.2791L11.937 8.78514L10.1274 9.63678ZM10.7116 1.69766C10.473 1.19008 10.0949 0.760894 9.6214 0.460274L7.47741 3.83715C7.30987 3.73078 7.17606 3.57891 7.09163 3.39931L10.7116 1.69766ZM9.6214 0.460274C9.14791 0.159653 8.59863 1.14441e-05 8.03777 1.14441e-05V4.00001C7.83931 4.00001 7.64495 3.94352 7.47741 3.83715L9.6214 0.460274ZM8.03777 1.14441e-05C7.4769 1.14441e-05 6.92763 0.159653 6.45414 0.460274L8.59813 3.83715C8.43059 3.94352 8.23623 4.00001 8.03777 4.00001V1.14441e-05ZM6.45414 0.460274C5.98065 0.760894 5.6025 1.19008 5.36391 1.69766L8.98391 3.39931C8.89948 3.57891 8.76567 3.73078 8.59813 3.83715L6.45414 0.460274ZM5.36427 1.69689L0.273359 12.5151L3.89263 14.2183L8.98354 3.40008L5.36427 1.69689ZM0.2569 12.551C-0.058543 13.2571 -0.0845037 14.0588 0.184593 14.7839L3.93468 13.3922C4.0299 13.6488 4.02071 13.9325 3.90909 14.1824L0.2569 12.551ZM0.184593 14.7839C0.453689 15.509 0.996384 16.0997 1.69615 16.4291L3.39982 12.8101C3.64743 12.9267 3.83946 13.1357 3.93468 13.3922L0.184593 14.7839ZM1.69615 16.4291C2.39592 16.7586 3.19695 16.8004 3.92726 16.5458L2.61035 12.7688C2.86876 12.6787 3.15221 12.6935 3.39982 12.8101L1.69615 16.4291ZM3.92726 16.5458C4.65756 16.2912 5.25891 15.7603 5.60217 15.0672L2.01769 13.292C2.13915 13.0467 2.35193 12.8589 2.61035 12.7688L3.92726 16.5458ZM5.61938 15.0316L6.85949 12.3979L3.24059 10.6939L2.00048 13.3276L5.61938 15.0316ZM5.05004 13.5459H11.0255V9.54587H5.05004V13.5459ZM9.21583 12.3974L10.4552 15.0312L14.0745 13.3281L12.8352 10.6943L9.21583 12.3974ZM10.4551 15.0311C10.7888 15.7402 11.3904 16.2877 12.1277 16.5532L13.4828 12.7897C13.7436 12.8836 13.9565 13.0773 14.0745 13.3281L10.4551 15.0311ZM12.1277 16.5532C12.865 16.8186 13.6776 16.7803 14.3867 16.4467L12.6836 12.8273C12.9345 12.7093 13.2219 12.6958 13.4828 12.7897L12.1277 16.5532ZM14.3867 16.4467C15.0957 16.113 15.6432 15.5114 15.9087 14.7741L12.1452 13.419C12.2391 13.1582 12.4328 12.9454 12.6836 12.8273L14.3867 16.4467ZM15.9087 14.7741C16.1742 14.0368 16.1359 13.2242 15.8022 12.5152L12.1829 14.2182C12.0648 13.9673 12.0513 13.6799 12.1452 13.419L15.9087 14.7741ZM15.8022 12.5151L10.7113 1.69689L7.092 3.40008L12.1829 14.2183L15.8022 12.5151ZM7.75865 10.4882L9.84751 6.04793L6.22803 4.34518L4.13917 8.7854L7.75865 10.4882ZM6.22815 6.04819L8.31781 10.4884L11.937 8.78514L9.84738 4.34491L6.22815 6.04819ZM10.1274 7.63678H5.94891V11.6368H10.1274V7.63678Z"
            fill={chroma(textOutlinePreviewColor).hex() === '#ffffff' ? '#f2f2f2' : textOutlinePreviewColor}
            mask="url(#path-1-outside-1_767_91)"
          />
        </svg>
      </div>
    </AlwanColorPicker>
  </div>
  <div class="flex" use:tooltip={'Outline Thickness'}>
    <button class="formatterBtn centerItem relative" on:click={openTextOutlineMenu} bind:this={container}>
      <svg width="18" height="24" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M1.925 2.8C1.78576 2.8 1.65223 2.85531 1.55377 2.95377C1.45531 3.05222 1.4 3.18576 1.4 3.325C1.4 3.46424 1.45531 3.59777 1.55377 3.69623C1.65223 3.79469 1.78576 3.85 1.925 3.85H12.075C12.2142 3.85 12.3478 3.79469 12.4462 3.69623C12.5447 3.59777 12.6 3.46424 12.6 3.325C12.6 3.18576 12.5447 3.05222 12.4462 2.95377C12.3478 2.85531 12.2142 2.8 12.075 2.8H1.925ZM1.4 6.475C1.4 5.992 1.792 5.6 2.275 5.6H11.725C11.9571 5.6 12.1796 5.69219 12.3437 5.85628C12.5078 6.02038 12.6 6.24293 12.6 6.475C12.6 6.70706 12.5078 6.92962 12.3437 7.09372C12.1796 7.25781 11.9571 7.35 11.725 7.35H2.275C1.792 7.35 1.4 6.958 1.4 6.475ZM1.4 10.325C1.4 9.6488 1.9488 9.1 2.625 9.1H11.375C11.6999 9.1 12.0115 9.22906 12.2412 9.45879C12.4709 9.68853 12.6 10.0001 12.6 10.325C12.6 10.6499 12.4709 10.9615 12.2412 11.1912C12.0115 11.4209 11.6999 11.55 11.375 11.55H2.625C2.30011 11.55 1.98853 11.4209 1.75879 11.1912C1.52906 10.9615 1.4 10.6499 1.4 10.325Z"
          fill="black"
        />
      </svg>
      {#if isTextOutlineThicknessMenuOpen}
        <div
          class="range absolute -bottom-10 -left-2 z-50 flex rounded-md px-2 py-4"
          on:click|stopPropagation
          on:keypress|stopPropagation
        >
          <input
            type="range"
            style={`background:linear-gradient(to right, #E24D72 ${t * 100}%, #ccc ${t * 100}%)`}
            min="0"
            max="1"
            step="0.01"
            bind:value={t}
            on:input={updateTextOutline}
          />
        </div>
      {/if}
    </button>
  </div>
</div>
<svelte:window on:click={detectWindowClick} />

<style lang="postcss">
  .gg-format-color {
    --ggs: 1.2;
    /* position: absolute;
      top: -4;
      right: 6; */
  }

  .range {
    width: 150px;
    background: #fff;
  }

  input[type='range'] {
    /* removing default appearance */
    -webkit-appearance: none;
    appearance: none;
    /* creating a custom design */
    width: 100%;
    cursor: pointer;
    outline: none;
    border-radius: 15px;
    /*  overflow: hidden;  remove this line*/

    /* New additions */
    height: 5px;
    background: #ccc;
  }

  /* Thumb: webkit */
  input[type='range']::-webkit-slider-thumb {
    /* removing default appearance */
    -webkit-appearance: none;
    appearance: none;
    /* creating a custom design */
    height: 15px;
    width: 15px;
    background-color: #ffdde5;
    border-radius: 50%;
    border: none;

    /* box-shadow: -407px 0 0 400px #f50; emove this line */
    transition: 0.2s ease-in-out;
  }

  /* Thumb: Firefox */
  input[type='range']::-moz-range-thumb {
    height: 15px;
    width: 15px;
    background-color: #ffdde5;
    border-radius: 50%;
    border: none;

    /* box-shadow: -407px 0 0 400px #f50; emove this line */
    transition: 0.2s ease-in-out;
  }
</style>
